/**
 * "The lens" panel on the dashboard. Leads with the simple three-lens promise
 * (operations / financials / AI posture) so a first-time user grasps the vision
 * in a few seconds: it mirrors the marketing copy (index.html, Login.tsx) and
 * the per-firm detail tabs. The dense five-axis structural framework is demoted
 * into a nested "full framework" disclosure, one click away for users who want
 * the deeper structural read. Axis definitions still come from lib/framework.ts,
 * the single source of truth shared with the Firm Framework tab.
 */
import { createSignal, For, Show } from "solid-js";

import { AXIS_DEFS } from "../lib/framework";

// First-run "how to use it" strip. Dismissed permanently per device so returning
// analysts are not re-taught (matches the fs-theme localStorage pattern in App.tsx).
const INTRO_KEY = "fs-intro-seen";

function introSeen(): boolean {
  try {
    return localStorage.getItem(INTRO_KEY) === "1";
  } catch {
    return false; // storage unreadable: show by default.
  }
}

function markIntroSeen() {
  try {
    localStorage.setItem(INTRO_KEY, "1");
  } catch {}
}

const LENSES = [
  {
    ix: "01",
    eyebrow: "HOW IT RUNS",
    title: "Operations",
    body: "What the firm sells, the scale it runs at, and the structural moats that hold.",
  },
  {
    ix: "02",
    eyebrow: "THE NUMBERS",
    title: "Financials",
    body: "Revenue, operating margin, free cash flow, and where it ranks against same-sector peers.",
  },
  {
    ix: "03",
    eyebrow: "DOES AI BREAK IT",
    title: "AI posture",
    body: "Which roles AI compresses out of the org, and which parts of the business structurally hold.",
  },
];

export default function FrameworkPanel() {
  const [introHidden, setIntroHidden] = createSignal(introSeen());
  const dismissIntro = () => {
    markIntroSeen();
    setIntroHidden(true);
  };

  return (
    <section class="fwp">
      <div class="fwp-lead">
        <span class="fwp-title">The lens</span>
        <span class="fwp-sub">
          Every Fortune 500 firm read three ways, against one structural question:
          what is the moat, and does AI compress this business.
        </span>
      </div>

      <div class="fwp-lenses">
        <For each={LENSES}>
          {(l) => (
            <div class="fwp-lens">
              <span class="ix">{l.ix} // {l.eyebrow}</span>
              <span class="h">{l.title}</span>
              <span class="p">{l.body}</span>
            </div>
          )}
        </For>
      </div>

      <Show when={!introHidden()}>
        <div class="fwp-howto">
          <div class="fwp-howto-head">
            <span class="fwp-title">How to use it</span>
            <button class="fwp-howto-dismiss" type="button" onClick={dismissIntro}>
              Got it, hide this
            </button>
          </div>
          <div class="fwp-lenses fwp-steps">
            <div class="fwp-lens">
              <span class="ix">01 // FIND</span>
              <span class="h">Find a firm</span>
              <span class="p">
                Jump to any firm with Cmd K search, or scan the firms grid (press g).
              </span>
            </div>
            <div class="fwp-lens">
              <span class="ix">02 // READ</span>
              <span class="h">Read it three ways</span>
              <span class="p">
                Open a firm and read its operations, financials, and AI posture.
                The verdict up top is the structural call.
              </span>
            </div>
            <div class="fwp-lens">
              <span class="ix">03 // COMPARE</span>
              <span class="h">Compare and scan</span>
              <span class="p">
                Check the same-sector peer table on each firm, or scan the revenue map
                below to see where margin concentrates.
              </span>
            </div>
          </div>
        </div>
      </Show>

      <details class="fwp-more">
        <summary class="fwp-more-summary">
          <span class="fwp-caret" aria-hidden="true">&#9656;</span>
          <span class="fwp-more-title">The full five-axis framework</span>
          <span class="fwp-sub">
            The structural read behind every verdict: clearing layer, verification, moats, compression, fork.
          </span>
        </summary>

        <div class="fwp-body">
          <dl class="fwp-axes">
            <For each={AXIS_DEFS}>
              {(ax, i) => (
                <div class="fwp-axis">
                  <span class="ix">{String(i() + 1).padStart(2, "0")} // {ax.label.toUpperCase()}</span>
                  <span class="h">{ax.definition}</span>
                  <span class="p"><b>Example</b> {ax.example}</span>
                </div>
              )}
            </For>
          </dl>

          <div class="fwp-posture">
            <span class="fwp-posture-lbl">AI posture</span>
            <span class="fwp-level"><strong>L1</strong> always human</span>
            <span class="fwp-level"><strong>L2</strong> AI drafts, human approves</span>
            <span class="fwp-level"><strong>L3</strong> AI runs, human supervises</span>
            <span class="fwp-level"><strong>L4</strong> fully autonomous</span>
          </div>

          <div class="fwp-verdict-head">
            <h3>Sample verdicts</h3>
            <span class="fwp-sub">
              Two beats: what holds, where pressure moves. Read these first.
            </span>
          </div>
          <div class="fwp-verdicts">
            <div class="fwp-v">
              <span class="firm">Walmart</span>
              <span class="tag">retail · physical infra</span>
              <span class="moat"><b>Moat</b> physical · distribution · vertical</span>
              <span class="risk"><b>AI risk</b> middle-office, merchandising</span>
              <span class="call">&#9656; Structural floor high. Margin moves to logistics agents.</span>
            </div>
            <div class="fwp-v">
              <span class="firm">JPMorgan Chase</span>
              <span class="tag">banking · regulated</span>
              <span class="moat"><b>Moat</b> regulatory · trust · balance sheet</span>
              <span class="risk"><b>AI risk</b> analysts, ops, mid-mgmt ~30% by 2027</span>
              <span class="call">&#9656; License is the moat. Reliability becomes the differentiator.</span>
            </div>
            <div class="fwp-v">
              <span class="firm">Boeing</span>
              <span class="tag">aerospace · capital-heavy</span>
              <span class="moat"><b>Moat</b> regulatory · capital · physical infra</span>
              <span class="risk"><b>AI risk</b> engineering bench ~25%</span>
              <span class="call">&#9656; Moats hold. Execution becomes the differentiator.</span>
            </div>
            <div class="fwp-v">
              <span class="firm">UnitedHealth</span>
              <span class="tag">healthcare · data-rich</span>
              <span class="moat"><b>Moat</b> regulatory · decision-trace data</span>
              <span class="risk"><b>AI risk</b> prior-auth, claims processing</span>
              <span class="call">&#9656; Claims data is the moat. Prior-auth denials are the political risk.</span>
            </div>
          </div>
        </div>
      </details>
    </section>
  );
}
